"use client";

import { SelectedFilterCount } from "@/components/common/selected-filter-count";
import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAtom } from "jotai";
import { useMemo, useState, useEffect } from "react";

const PRICE_PRESETS = [
  { label: "Under $100K", max: BigInt(100_000), min: null },
  { label: "$100K – $250K", min: BigInt(100_000), max: BigInt(250_000) },
  { label: "$250K – $500K", min: BigInt(250_000), max: BigInt(500_000) },
  { label: "$500K – $1M", min: BigInt(500_000), max: BigInt(1_000_000) },
  { label: "$1M – $5M", min: BigInt(1_000_000), max: BigInt(5_000_000) },
  { label: "$5M+", min: BigInt(5_000_000), max: null },
] as const;

function computePresetCount(
  preset: (typeof PRICE_PRESETS)[number],
  rangeCounts: { total: number; gte: Record<string, number>; lte: Record<string, number> },
): number {
  if (preset.min === null && preset.max !== null) {
    return rangeCounts.lte[String(preset.max)] ?? 0;
  }
  if (preset.min !== null && preset.max === null) {
    return rangeCounts.gte[String(preset.min)] ?? 0;
  }
  if (preset.min !== null && preset.max !== null) {
    const gteMin = rangeCounts.gte[String(preset.min)] ?? 0;
    const lteMax = rangeCounts.lte[String(preset.max)] ?? 0;
    return Math.max(0, gteMin + lteMax - rangeCounts.total);
  }
  return rangeCounts.total;
}

function formatBigIntInput(val: bigint | null): string {
  if (val === null) return "";
  return val.toString();
}

export function PriceRangeFilterPopover() {
  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const facets = useListingFacetCounts();

  const filtersCount = useMemo(() => {
    return filters.priceRange.min !== null || filters.priceRange.max !== null ? 1 : 0;
  }, [filters.priceRange]);

  const presetCounts = useMemo(() => {
    const rc = facets.data?.rangeCounts?.price;
    if (!rc) return null;
    return PRICE_PRESETS.map((preset) => computePresetCount(preset, rc));
  }, [facets.data?.rangeCounts?.price]);

  const [customMin, setCustomMin] = useState(formatBigIntInput(filters.priceRange.min as bigint | null));
  const [customMax, setCustomMax] = useState(formatBigIntInput(filters.priceRange.max as bigint | null));

  useEffect(() => {
    setCustomMin(formatBigIntInput(filters.priceRange.min as bigint | null));
    setCustomMax(formatBigIntInput(filters.priceRange.max as bigint | null));
  }, [filters.priceRange]);

  function selectPreset(preset: typeof PRICE_PRESETS[number]) {
    setFilters((prev) => ({
      ...prev,
      priceRange: { min: preset.min ?? null, max: preset.max ?? null },
    }));
  }

  function isPresetActive(preset: typeof PRICE_PRESETS[number]) {
    const minMatch = preset.min === null
      ? filters.priceRange.min === null
      : filters.priceRange.min === preset.min;
    const maxMatch = preset.max === null
      ? filters.priceRange.max === null
      : filters.priceRange.max === preset.max;
    return minMatch && maxMatch;
  }

  function applyCustomRange() {
    const min = customMin ? BigInt(customMin) : null;
    const max = customMax ? BigInt(customMax) : null;
    setFilters((prev) => ({
      ...prev,
      priceRange: { min, max },
    }));
  }

  function resetPrice() {
    setFilters((prev) => ({
      ...prev,
      priceRange: { min: null, max: null },
    }));
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="min-w-0 h-full justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {filtersCount > 0 && <SelectedFilterCount count={filtersCount} />}
            <span className="overflow-hidden text-ellipsis">Price Range</span>
          </div>
          <Icon icon="ChevronDown" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[360px] p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="text-sm font-medium">Price Range</div>
          {filtersCount > 0 && (
            <button
              type="button"
              onClick={resetPrice}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="flex flex-col gap-3 p-4">
          <div className="grid grid-cols-2 gap-2">
            {PRICE_PRESETS.map((preset, idx) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => selectPreset(preset)}
                className={cn(
                  "flex min-h-11 md:min-h-10 items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border transition-colors text-left",
                  isPresetActive(preset)
                    ? "border-foreground bg-muted font-medium"
                    : "border-border hover:border-border hover:bg-secondary",
                )}
              >
                <span
                  className="min-w-0 flex-1 truncate whitespace-nowrap"
                  title={preset.label}
                >
                  {preset.label}
                </span>
                {facets.isLoading ? (
                  <span className="inline-block h-3 w-5 shrink-0 animate-pulse rounded bg-muted" />
                ) : presetCounts ? (
                  <span className="shrink-0 font-data text-[11px] tabular-nums text-muted-foreground">
                    {presetCounts[idx]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-muted" />
            <span className="text-xs text-muted-foreground">or custom</span>
            <div className="h-px flex-1 bg-muted" />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1">Min ($)</Label>
              <Input
                type="number"
                placeholder="No min"
                value={customMin}
                onChange={(e) => setCustomMin(e.target.value)}
                onBlur={applyCustomRange}
                onKeyDown={(e) => e.key === "Enter" && applyCustomRange()}
                className="h-9 text-sm font-data"
              />
            </div>
            <span className="text-muted-foreground mt-5">—</span>
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1">Max ($)</Label>
              <Input
                type="number"
                placeholder="No max"
                value={customMax}
                onChange={(e) => setCustomMax(e.target.value)}
                onBlur={applyCustomRange}
                onKeyDown={(e) => e.key === "Enter" && applyCustomRange()}
                className="h-9 text-sm font-data"
              />
            </div>
          </div>

        </div>
      </PopoverContent>
    </Popover>
  );
}
